import React, { ReactNode } from "react";

import Box from "veneer/Box";
import { Breadcrumb } from "veneer/Breadcrumbs";
import Skeleton from "veneer/Skeleton";
import Stack from "veneer/Stack";
import { TabsProvider } from "veneer/Tabs";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { ENV } from "cortex/ui/src/constant/app";
import { useNavBarContext } from "cortex/ui/src/navigation/NavBarContext";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { WsSessionStatus } from "cortex/ui/src/ui/ws-session-status";

import { Footer } from "./Footer";
import { Header } from "./Header";
import { Panel } from "./Panel";

const appName = ENV.APP_NAME;

interface ContainerProps {
  breadcrumbs?: Breadcrumb[];
  children: ReactNode;
  desc?: ReactNode;
  hideBox?: boolean;
  gap?: number;
  p?: number;
  spacing?: number;
  title?: string;
  toolbox?: ReactNode;
  width?: number | string;
}

function Container({
  breadcrumbs,
  children,
  desc,
  gap,
  hideBox,
  p = 0,
  spacing,
  title,
  toolbox,
  width,
}: ContainerProps) {
  const { expanded } = useNavBarContext();
  const pageTitle = <PageTitle desc={desc} title={title} toolbox={toolbox} />;

  return (
    <div className={`content-wrapper ${expanded ? "expanded" : "collapsed"}`}>
      <Stack bgcolor={Colors.gray200} minHeight="100vh">
        <TabsProvider>
          <Header breadcrumbs={breadcrumbs || []} title={pageTitle} />
          <Stack p={2} pb={5} spacing={2} width="100%">
            <WsSessionStatus />
            {!hideBox ? (
              <Panel gap={gap} p={p} spacing={spacing} width={width}>
                {children}
              </Panel>
            ) : (
              children
            )}
          </Stack>
        </TabsProvider>
        <Footer />
      </Stack>
    </div>
  );
}

interface PageTitleProps {
  desc?: ReactNode;
  title?: string;
  toolbox?: ReactNode;
}

const PageTitle = ({ desc, title, toolbox }: PageTitleProps) => {
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      {!title ? (
        <Skeleton width={400} />
      ) : (
        <Stack alignItems="center" direction="row" spacing={2}>
          <Box>{title}</Box>
          <Typography variant="h6">{desc}</Typography>
        </Stack>
      )}
      <Stack alignItems="center" direction="row" spacing={2}>
        {appName === "fleet" && <NavLoader />}
        {toolbox}
      </Stack>
    </Stack>
  );
};

export { Container };
export default Container;
